/** One cited passage returned by the Platform MCP `search_speakeasy_docs` tool. */
export interface DocsExcerpt {
  uri: string;
  title: string;
  heading?: string;
  excerpt: string;
  source: string;
  owner?: string;
  observed_at?: string;
  revalidate_by?: string;
  stale?: boolean;
  links?: string[];
  /** The guide's published page on the Speakeasy documentation site. */
  docs_url?: string;
}

/** Reviewed setup guides are addressed under this scheme. */
const SETUP_RESOURCE_PREFIX = "speakeasy://platform-mcp/setup/";

/** How deep to walk a tool result looking for citations. */
const MAX_SCAN_DEPTH = 6;

function isDocsExcerpt(value: unknown): value is DocsExcerpt {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.uri === "string" &&
    candidate.uri.startsWith(SETUP_RESOURCE_PREFIX) &&
    typeof candidate.title === "string" &&
    typeof candidate.excerpt === "string" &&
    typeof candidate.source === "string"
  );
}

/** One guide the assistant opened in full. */
export interface ReadDoc {
  uri: string;
  title?: string;
  text: string;
}

/**
 * Finds the guides a run opened in full, by the same structural walk.
 *
 * A search hands back everything that matched the query, which for a generic
 * query is most of the corpus; the guides the assistant then read are the ones
 * its answer came from. Callers use this to show what was used rather than
 * everything that was seen.
 */
export function findReadDocs(result: unknown): ReadDoc[] {
  const docs: ReadDoc[] = [];
  const seen = new Set<string>();

  const walk = (value: unknown, depth: number): void => {
    if (depth > MAX_SCAN_DEPTH || value === null) return;

    if (typeof value === "string") {
      if (!value.includes(SETUP_RESOURCE_PREFIX)) return;
      try {
        walk(JSON.parse(value), depth + 1);
      } catch {
        // Not JSON.
      }
      return;
    }

    if (Array.isArray(value)) {
      for (const item of value) walk(item, depth + 1);
      return;
    }

    if (typeof value !== "object") return;

    const candidate = value as Record<string, unknown>;
    // A read carries the whole guide; a search hit carries an excerpt of one.
    if (
      typeof candidate.uri === "string" &&
      candidate.uri.startsWith(SETUP_RESOURCE_PREFIX) &&
      typeof candidate.text === "string" &&
      candidate.text !== ""
    ) {
      if (!seen.has(candidate.uri)) {
        seen.add(candidate.uri);
        docs.push({
          uri: candidate.uri,
          title:
            typeof candidate.title === "string" ? candidate.title : undefined,
          text: candidate.text,
        });
      }
      return;
    }

    for (const item of Object.values(candidate)) walk(item, depth + 1);
  };

  walk(result, 0);
  return docs;
}

/**
 * Builds a citation from a guide that was read without a search behind it.
 *
 * A search hit arrives with its metadata already separated; a read arrives as
 * one markdown document whose citation header carries the same facts. Parsing
 * the header back out means a guide the assistant opened directly still shows
 * a card, rather than the card depending on how the assistant happened to find
 * it.
 */
export function excerptFromReadDoc(doc: ReadDoc): DocsExcerpt {
  const field = (label: string): string => {
    const match = doc.text.match(new RegExp(`^- ${label}: (.+)$`, "m"));
    return match?.[1]?.trim() ?? "";
  };
  // The header ends at the rule the corpus writes between it and the guide.
  const [, body = doc.text] = doc.text.split("\n---\n\n");
  const heading = body.match(/^#{1,6}\s+(.+)$/m)?.[1]?.trim();

  return {
    uri: doc.uri,
    title: doc.title ?? doc.text.match(/^#\s+(.+)$/m)?.[1]?.trim() ?? doc.uri,
    heading: heading && !heading.startsWith("Owner") ? heading : undefined,
    excerpt: body.replace(/^#{1,6}\s+.+$/m, "").trim(),
    // "mcp-setup-docs/go@v0.3.0 (pinned reviewed export; …)" — the version is
    // the citation; the parenthetical is prose for a reader of the guide.
    source: field("Source").replace(/\s*\(.*\)$/, ""),
    docs_url: field("Speakeasy docs"),
    observed_at: field("Observed"),
    revalidate_by: field("Revalidate by"),
    links: doc.text
      .split("\n")
      .map((line) => line.match(/^\s+- (https:\/\/\S+)$/)?.[1])
      .filter((link): link is string => Boolean(link)),
  };
}

/**
 * Finds documentation citations anywhere in a tool result.
 *
 * The search is structural rather than keyed on the tool's name because the
 * assistant runtime does not call tools one per message part: it runs a
 * `compose` script that may call several tools and return their results under
 * names the script chose. Keying on `search_speakeasy_docs` would therefore never
 * match on this surface, while the citations themselves are unmistakable — an
 * excerpt carries a speakeasy:// setup URI, its source, and its text.
 *
 * Returns an empty array for anything else, including a `guide_unavailable`
 * answer, so the caller falls back to the generic result view rather than
 * showing an empty citation strip that reads like a sourceless answer.
 */
export function findDocsExcerpts(result: unknown): DocsExcerpt[] {
  const found: DocsExcerpt[] = [];
  const seen = new Set<string>();

  const walk = (value: unknown, depth: number): void => {
    if (depth > MAX_SCAN_DEPTH || value === null) return;

    if (typeof value === "string") {
      // A compose step hands a tool's JSON payload back as a string when the
      // script returns it verbatim, so the citations arrive encoded.
      if (!value.includes(SETUP_RESOURCE_PREFIX)) return;
      try {
        walk(JSON.parse(value), depth + 1);
      } catch {
        // Not JSON — prose that merely mentions a URI carries no citation.
      }
      return;
    }

    if (Array.isArray(value)) {
      for (const item of value) walk(item, depth + 1);
      return;
    }

    if (typeof value !== "object") return;

    if (isDocsExcerpt(value)) {
      const key = value.uri + " " + (value.heading ?? "");
      if (!seen.has(key)) {
        seen.add(key);
        found.push(value);
      }
      return;
    }

    for (const item of Object.values(value as Record<string, unknown>)) {
      walk(item, depth + 1);
    }
  };

  walk(result, 0);
  return found;
}

/**
 * Renders guide markdown as the plain text a preview needs.
 *
 * The excerpt is markdown, and a clamped preview of it shows the syntax rather
 * than the sentence — "click **Settings**" reads worse than "click Settings".
 * Only inline emphasis, code ticks, and link syntax are unwrapped; the full
 * guide keeps its formatting when the reader opens it.
 */
export function previewText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^\s*#{1,6}\s*/gm, "")
    .replace(/^\s*[-*]\s+/gm, "")
    .replace(/\s*\n\s*/g, " ")
    .trim();
}

/** The bare host, as a link preview shows it: "speakeasy.com", not
 * "www.speakeasy.com". The subdomain is routing detail, not identity. */
export function displayHost(link: string): string {
  try {
    return new URL(link).host.replace(/^www\./, "");
  } catch {
    return link;
  }
}

/** Accepts a link only when it is an absolute https URL, which is the only
 * shape a citation should ever produce and the only one safe to put in an
 * anchor. Anything else — another scheme, a relative path, an unparseable
 * string — is dropped rather than rendered. */
export function httpsURL(link: string | undefined): string | undefined {
  if (!link) return undefined;
  try {
    return new URL(link).protocol === "https:" ? link : undefined;
  } catch {
    return undefined;
  }
}
